/**
 * Kits e preço: todos os kits com o custo total (custo do base × unidades) e o preço e a margem em cada canal.
 * Filtra por nome, departamento, categoria, subcategoria, marca e unidades; aplica uma margem final aos kits
 * selecionados (ou a todos do filtro) e gera as fotos dos kits que ainda não têm.
 * Alertas (com filtro): margem negativa, custo acima do preço na Shopee e unidade do kit mais cara que o produto base.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Percent, Search, X } from 'lucide-react';
import { api, brl, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { Modal } from './CatalogSettings.tsx';
import { Pager, useSearchBox, useUrlState } from './MeliChannel.tsx';

interface Account { id: string; channel: string; name: string | null }
interface KitOffer { price: number | null; marginPct: number | null; source: string; rule: number | null }
interface KitRow {
  id: string; sku: string; title: string | null; units: number; discountPct: number | null; active: boolean;
  category: string | null; brand: string | null; photos: number; available: number;
  base: { id: string; sku: string; title: string | null } | null;
  unitCost: number | null; totalCost: number | null; totalPmc: number | null;
  offers: Record<string, KitOffer>;
  /** Preço do produto base (o "pai") em cada conta. */
  basePrices: Record<string, number | null>;
}
type KitAlert = 'negative' | 'shopee_cost' | 'unit_above_base';
interface KitPage { accounts: Account[]; alerts: Record<KitAlert, number>; total: number; page: number; pageSize: number; items: KitRow[] }
const ALERTS: { key: KitAlert; label: string; note: string }[] = [
  { key: 'negative', label: 'Margem negativa', note: 'prejuízo em algum canal' },
  { key: 'shopee_cost', label: 'Custo acima do preço na Shopee', note: 'custo do kit maior que o preço de venda' },
  { key: 'unit_above_base', label: 'Unidade do kit mais cara', note: 'preço ÷ unidades acima do preço do produto base' },
];
interface Facets { department: { value: string; count: number }[]; category: { value: string; count: number }[]; subcategory: { value: string; count: number }[]; brand: { value: string; count: number }[] }

const CH: Record<string, string> = { mercadolivre: 'Meli', shopee: 'Shopee', ifood: 'iFood' };
const FILTERS = ['q', 'department', 'category', 'subcategory', 'brand', 'units', 'noPhoto', 'alert'] as const;
type Selection = { kitIds: string[]; label: string } | { filter: Record<string, string>; label: string };

const pct = (v: number | null) => (v == null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`);

export function KitsPage() {
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [margin, setMargin] = useState<Selection | null>(null);
  const qc = useQueryClient();
  const page = Math.max(1, Number(params.get('page') ?? 1));
  const filter: Record<string, string> = {};
  for (const k of FILTERS) { const v = params.get(k); if (v) filter[k] = v; }
  const query = new URLSearchParams({ ...filter, page: String(page), pageSize: '50' });
  const list = useQuery({ queryKey: ['kits', query.toString()], queryFn: () => api<KitPage>(`/catalog/kits?${query}`), placeholderData: keepPreviousData });
  const facets = useQuery({ queryKey: ['facets'], queryFn: () => api<Facets>('/catalog/facets'), staleTime: 300_000 });
  const photos = useMutation({
    mutationFn: (sel: Selection) => api<{ queued: number }>('/catalog/kits/photos', { method: 'POST', json: 'kitIds' in sel ? { kitIds: sel.kitIds } : { filter: sel.filter } }),
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['kits'] }), 4000),
  });

  const d = list.data;
  const accounts = d?.accounts ?? [];
  const filtered = Object.keys(filter).length > 0;
  const selection = (): Selection => selected.size
    ? { kitIds: [...selected], label: `${selected.size} selecionado(s)` }
    : { filter, label: filtered ? `Filtro atual (${int(d?.total ?? 0)})` : `Todos os kits ativos (${int(d?.total ?? 0)})` };
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOnPage = !!d?.items.length && d.items.every((k) => selected.has(k.id));
  const facet = (key: keyof Facets, label: string) => (
    <select className="select" aria-label={label} value={params.get(key) ?? ''} onChange={(e) => set({ [key]: e.target.value || null, page: null })}>
      <option value="">{label}: todos</option>
      {facets.data?.[key].map((f) => <option key={f.value} value={f.value}>{f.value} ({int(f.count)})</option>)}
    </select>
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Kits e preço</h1>
          <p>Custo total do kit (custo do produto base × unidades), preço e margem em cada canal. Selecione kits e aplique a margem final que quer ter: o preço passa a sair dela e acompanha o custo do base.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" type="button" disabled={photos.isPending} onClick={() => photos.mutate(selection())}><ImageIcon size={16} /> Gerar fotos dos sem foto</button>
          <button className="btn primary" type="button" disabled={!d?.total} onClick={() => setMargin(selection())}><Percent size={16} /> Aplicar margem</button>
        </div>
      </div>
      {photos.data && <div className="notice ok" style={{ marginBottom: 12 }}>{photos.data.queued ? `${int(photos.data.queued)} foto(s) de kit sendo montada(s). Atualize a página em alguns instantes.` : 'Nenhum kit sem foto na seleção.'}</div>}
      {photos.error && <ErrorBox error={photos.error} />}

      {d && (
        <div className="kpis" style={{ marginBottom: 12 }}>
          {ALERTS.map((a) => (
            <button key={a.key} type="button" className={`kpi${params.get('alert') === a.key ? ' active' : ''}${d.alerts[a.key] > 0 ? ' alert' : ''}`}
              style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
              onClick={() => { set({ alert: params.get('alert') === a.key ? null : a.key, page: null }); setSelected(new Set()); }}>
              <span className="k-label">{a.label}</span><span className="k-value">{int(d.alerts[a.key])}</span><span className="k-note">{a.note}</span>
            </button>
          ))}
        </div>
      )}

      <div className="toolbar">
        <label className="search">
          <Search size={16} />
          <span className="sr-only">Buscar</span>
          <input className="input" type="search" placeholder="Nome ou SKU do kit ou do produto base" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        {facet('department', 'Departamento')}
        {facet('category', 'Categoria')}
        {facet('subcategory', 'Subcategoria')}
        {facet('brand', 'Marca')}
        <select className="select" aria-label="Unidades" value={params.get('units') ?? ''} onChange={(e) => set({ units: e.target.value || null, page: null })}>
          <option value="">Qualquer kit</option>
          {[2, 3, 4, 5, 6, 8, 10, 12].map((u) => <option key={u} value={u}>Kit de {u}</option>)}
        </select>
        <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" className="check" checked={params.get('noPhoto') === 'true'} onChange={(e) => set({ noPhoto: e.target.checked ? 'true' : null, page: null })} /> Sem foto
        </label>
      </div>

      <div className="panel flush">
        {selected.size > 0 && (
          <div className="bulkbar" role="status">
            <b>{selected.size} selecionado{selected.size > 1 ? 's' : ''}</b>
            <span style={{ flex: 1 }} />
            <button className="btn sm" type="button" onClick={() => setMargin(selection())}><Percent size={14} /> Aplicar margem</button>
            <button className="btn sm" type="button" onClick={() => photos.mutate(selection())}><ImageIcon size={14} /> Gerar fotos</button>
            <button className="btn sm quiet" type="button" onClick={() => setSelected(new Set())}><X size={14} /> Limpar</button>
          </div>
        )}
        {list.error ? <div style={{ padding: 18 }}><ErrorBox error={list.error} /></div>
          : !d ? <Loading />
          : d.items.length === 0 ? <div className="empty"><b>Nenhum kit encontrado</b>Crie kits em lote no Catálogo (botão "Criar kits").</div> : (
          <div className="grid-wrap" style={{ opacity: list.isPlaceholderData ? 0.55 : 1 }}>
            <table className="grid" style={{ width: '100%', minWidth: 900 + accounts.length * 150 }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}><input type="checkbox" className="check" aria-label="Marcar a página" checked={allOnPage}
                    onChange={() => setSelected((s) => { const n = new Set(s); for (const k of d.items) { if (allOnPage) n.delete(k.id); else n.add(k.id); } return n; })} /></th>
                  <th>Kit</th>
                  <th style={{ width: 80 }} className="num">Unid.</th>
                  <th style={{ width: 120 }} className="num">Custo unit.</th>
                  <th style={{ width: 130 }} className="num">Custo total</th>
                  <th style={{ width: 120 }} className="num">Disponível</th>
                  {accounts.map((a) => <th key={a.id} style={{ width: 150 }} className="num">{CH[a.channel] ?? a.channel}{accounts.filter((x) => x.channel === a.channel).length > 1 ? ` · ${a.name}` : ''}</th>)}
                </tr>
              </thead>
              <tbody>
                {d.items.map((k) => (
                  <tr key={k.id} className={selected.has(k.id) ? 'selected' : undefined} onClick={() => toggle(k.id)} style={{ cursor: 'pointer' }}>
                    <td onClick={(e) => e.stopPropagation()}><input type="checkbox" className="check" aria-label="Marcar" checked={selected.has(k.id)} onChange={() => toggle(k.id)} /></td>
                    <td>
                      <div className="cell-2">
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Link to={`/catalogo/${k.id}`} onClick={(e) => e.stopPropagation()}>{k.title ?? k.sku}</Link>
                          {!k.photos && <> <Tag tone="warn">sem foto</Tag></>}
                        </span>
                        <span className="sub"><span className="code">{k.sku}</span>{k.base && <> · base <span className="code">{k.base.sku}</span></>}{k.category ? ` · ${k.category}` : ''}</span>
                      </div>
                    </td>
                    <td className="num">{k.units}</td>
                    <td className="num">{brl(k.unitCost)}</td>
                    <td className="num"><b>{brl(k.totalCost)}</b></td>
                    <td className="num">{int(k.available)}</td>
                    {accounts.map((a) => {
                      const o = k.offers[a.id];
                      const basePrice = k.basePrices[a.id] ?? null;
                      const unit = o?.price != null ? o.price / k.units : null;
                      const belowCost = a.channel === 'shopee' && o?.price != null && k.totalCost != null && o.price < k.totalCost;
                      const unitAbove = unit != null && basePrice != null && unit > basePrice + 0.009;
                      return (
                        <td key={a.id} className="num">
                          <div className="cell-2" style={{ alignItems: 'flex-end' }}>
                            <span style={belowCost ? { color: 'var(--err)', fontWeight: 600 } : undefined} title={belowCost ? `Custo do kit ${brl(k.totalCost)} acima do preço na Shopee` : undefined}>
                              {o?.price != null ? brl(o.price) : '—'}
                            </span>
                            <span className="sub" style={{ color: o?.marginPct != null && o.marginPct < 0 ? 'var(--err)' : undefined }}>
                              {o?.rule != null ? <Tag tone="info">margem {pct(o.rule)}</Tag> : null} {pct(o?.marginPct ?? null)}
                            </span>
                            {unitAbove && (
                              <span className="sub" style={{ color: 'var(--warn)' }} title={`Unidade no kit ${brl(unit)} × produto base ${brl(basePrice)}`}>
                                unid. {brl(unit)} &gt; base {brl(basePrice)}
                              </span>
                            )}
                            {belowCost && <span className="sub" style={{ color: 'var(--err)' }}>abaixo do custo</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {d && <Pager total={d.total} page={page} pageSize={d.pageSize} onPage={(p) => set({ page: p > 1 ? String(p) : null })} />}
      </div>
      {margin && <MarginDialog selection={margin} accounts={accounts} onClose={() => setMargin(null)} />}
    </>
  );
}

function MarginDialog({ selection, accounts, onClose }: { selection: Selection; accounts: Account[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [chosen, setChosen] = useState<Set<string>>(new Set(accounts.map((a) => a.id)));
  const [value, setValue] = useState('');
  const run = useMutation({
    mutationFn: (marginPct: number | null) => api<{ kits: number; accounts: number }>('/catalog/kits/margin', {
      method: 'POST',
      json: { ...('kitIds' in selection ? { kitIds: selection.kitIds } : { filter: selection.filter }), accountIds: [...chosen], marginPct },
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kits'] }),
  });
  const m = Number(value.replace(',', '.'));
  const valid = value.trim() !== '' && Number.isFinite(m) && m > -50 && m < 90;
  return (
    <Modal title="Margem final dos kits" onClose={onClose}>
      {run.data ? (
        <>
          <p style={{ margin: 0 }}>Preço recalculado em <b>{int(run.data.kits)} kit(s)</b>, em {run.data.accounts} canal(is). O envio para os canais segue a fila normal de preço e estoque.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="btn primary" type="button" onClick={onClose}>Concluir</button></div>
        </>
      ) : (
        <>
          <p style={{ margin: 0 }}>Kits: <b>{selection.label}</b>.</p>
          <p className="small muted" style={{ margin: 0 }}>
            Margem sobre o preço de venda, depois da comissão e das taxas de cada canal (as da configuração do canal). O preço passa a sair
            desta margem e do custo do base × unidades, e acompanha o custo quando ele mudar. Preço digitado na mão nesses kits é substituído.
          </p>
          <div className="field"><label>Canais</label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {accounts.map((a) => (
                <label key={a.id} className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" className="check" checked={chosen.has(a.id)} onChange={() => setChosen((s) => { const n = new Set(s); if (n.has(a.id)) n.delete(a.id); else n.add(a.id); return n; })} />
                  {CH[a.channel] ?? a.channel}{a.name ? ` · ${a.name}` : ''}
                </label>
              ))}
            </div>
          </div>
          <div className="field"><label htmlFor="kit-margin">Margem final (%)</label>
            <input id="kit-margin" className="input" style={{ width: 140 }} inputMode="decimal" placeholder="ex.: 15" value={value} onChange={(e) => setValue(e.target.value)} /></div>
          {run.error && <ErrorBox error={run.error} />}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn quiet" type="button" disabled={!chosen.size || run.isPending} onClick={() => run.mutate(null)} title="Remove a margem própria: o preço volta a ser o do base × unidades − desconto">Voltar ao preço do base</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" type="button" onClick={onClose}>Cancelar</button>
              <button className="btn primary" type="button" disabled={!valid || !chosen.size || run.isPending} onClick={() => run.mutate(m)}><Percent size={16} /> {run.isPending ? 'Aplicando…' : 'Aplicar margem'}</button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
